import React from 'react';
import { ProjetCalcule } from '../types';
import ProjetProgressBar from './ProjetProgressBar';
import {
  ArrowLeftIcon,
  MapPinIcon,
  UserIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface SuiviChantierHeaderProps {
  projet: ProjetCalcule;
  onRetour: () => void;
}

const fmt = (n: number) => n.toLocaleString('fr-FR');

const SuiviChantierHeader: React.FC<SuiviChantierHeaderProps> = ({ projet, onRetour }) => {
  const ecartBudget = projet.budgetActuel - projet.budgetSigne;

  return (
    <div className="mb-8">
      {/* Retour */}
      <button
        onClick={onRetour}
        className="flex items-center text-violet-600 hover:text-violet-800 mb-4 text-sm font-medium transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4 mr-1" />
        Retour au tableau de bord
      </button>

      {/* Résumé projet */}
      <section className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-xl p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{projet.nom}</h2>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
              <span className="flex items-center">
                <MapPinIcon className="w-4 h-4 mr-1" />
                {projet.adresse}
              </span>
              <span className="flex items-center">
                <UserIcon className="w-4 h-4 mr-1" />
                {projet.client}
              </span>
              <span className="flex items-center">
                <CalendarDaysIcon className="w-4 h-4 mr-1" />
                {new Date(projet.dateDebut).toLocaleDateString('fr-FR')} - {new Date(projet.dateFin).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div>
            <div className="text-sm text-gray-500">Montant contractuel</div>
            <div className="text-xl font-semibold text-gray-900">{fmt(projet.budgetSigne)} €</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Budget modifié</div>
            <div className="text-xl font-semibold text-gray-900">{fmt(projet.budgetActuel)} €</div>
            {ecartBudget > 0 && (
              <div className="text-xs text-red-600">+{fmt(ecartBudget)} € de TS</div>
            )}
          </div>
          <div>
            <div className="text-sm text-gray-500">Avancement facturé</div>
            <div className="text-xl font-semibold text-gray-900">{fmt(projet.cumulTotal)} €</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Marge globale</div>
            <div className={`text-xl font-semibold ${projet.margeGlobale >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {projet.margeGlobale >= 0 ? '+' : ''}{fmt(Math.round(projet.margeGlobale))} €
            </div>
            <div className={`text-xs ${projet.tauxMargeGlobal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {projet.tauxMargeGlobal.toFixed(1)}%
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              MAT
              {projet.mat.tendance === 'amelioration' && <ArrowTrendingUpIcon className="w-3.5 h-3.5 text-emerald-500" />}
              {projet.mat.tendance === 'degradation' && <ArrowTrendingDownIcon className="w-3.5 h-3.5 text-red-500" />}
              {projet.mat.tendance === 'stable' && <MinusIcon className="w-3.5 h-3.5 text-gray-400" />}
              {projet.incidentsOuverts.length > 0 && (
                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-600">
                  <ExclamationTriangleIcon className="w-3 h-3 mr-0.5" />
                  {projet.incidentsOuverts.length}
                </span>
              )}
            </div>
            <div className={`text-xl font-semibold ${projet.mat.margeATerminaison >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {projet.mat.margeATerminaison >= 0 ? '+' : ''}{fmt(Math.round(projet.mat.margeATerminaison))} €
            </div>
            <div className={`text-xs ${projet.mat.tauxMAT >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {projet.mat.tauxMAT.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Progress bars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <ProjetProgressBar
            pourcentage={projet.pourcentageFinancier}
            label="Avancement financier"
            color="bg-violet-600"
          />
          <ProjetProgressBar
            pourcentage={projet.pourcentageTemporel}
            label="Avancement temporel"
            color="bg-blue-500"
          />
        </div>
      </section>
    </div>
  );
};

export default SuiviChantierHeader;
